import VectorSource from 'ol/source/Vector.js';
import VectorLayer from "ol/layer/Vector.js";
import Draw from 'ol/interaction/Draw.js';
import Overlay from 'ol/Overlay.js';
import Polygon, { fromExtent } from 'ol/geom/Polygon.js';
import { buffer as bufferExtent } from 'ol/extent.js';
import { unByKey } from 'ol/Observable.js';
import { mapActions, mapState } from 'vuex';
import Style from 'ol/style/Style.js';
import Stroke from 'ol/style/Stroke.js';

// Persistent state across component mounts
let persistentDrawSource = null;
let persistentDrawLayer = null;
// Mapa OL dueño de la capa persistida. mapService.destroy() (MapContainer.vue)
// tira el mapa entero en cada salida del visor y se construye uno nuevo al
// entrar, así que la capa heredada hay que compararla contra el mapa actual.
let persistentDrawMap = null;

// Ellipse Geometry Function: Rotated version (64 points)
const ellipseGeometryFunction = (coordinates, geometry) => {
    if (!geometry) {
        geometry = new Polygon([]);
    }
    const center = coordinates[0];
    const corner = coordinates[1];
    const dx = corner[0] - center[0];
    const dy = corner[1] - center[1];
    
    const rx = Math.hypot(dx, dy);
    const ry = rx * 0.5; // Fixed aspect ratio for visual consistency
    const rotation = Math.atan2(dy, dx);
    const minorEnd = [
        center[0] - Math.sin(rotation) * ry,
        center[1] + Math.cos(rotation) * ry
    ];
    
    const points = [];
    const numPoints = 64;
    const cos = Math.cos(rotation);
    const sin = Math.sin(rotation);

    for (let i = 0; i < numPoints; i++) {
        const angle = (i / numPoints) * 2 * Math.PI;
        const x = rx * Math.cos(angle);
        const y = ry * Math.sin(angle);
        
        // Apply rotation and translate to center
        points.push([
            center[0] + x * cos - y * sin,
            center[1] + x * sin + y * cos
        ]);
    }
    points.push(points[0]);
    geometry.setProperties({
        ellipseCenter: center.slice(),
        ellipseMajorEnd: corner.slice(),
        ellipseMinorEnd: minorEnd
    }, true);
    geometry.setCoordinates([points]);
    return geometry;
};

// Arrow Geometry Function: 7-point polygon
const arrowGeometryFunction = (coordinates, geometry) => {
    if (!geometry) {
        geometry = new Polygon([]);
    }
    const start = coordinates[0];
    const end = coordinates[1];
    const dx = end[0] - start[0];
    const dy = end[1] - start[1];
    const length = Math.hypot(dx, dy);
    
    if (length === 0) return geometry;
    
    const angle = Math.atan2(dy, dx);
    const headLen = length * 0.25; 
    const headWid = length * 0.15;
    const shaftWid = length * 0.05;

    // Define arrow shape in local coordinates
    const points = [
        [0, -shaftWid / 2],
        [length - headLen, -shaftWid / 2],
        [length - headLen, -headWid / 2],
        [length, 0],
        [length - headLen, headWid / 2],
        [length - headLen, shaftWid / 2],
        [0, shaftWid / 2],
        [0, -shaftWid / 2]
    ];

    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    const transformedPoints = points.map(p => [
        start[0] + p[0] * cos - p[1] * sin,
        start[1] + p[0] * sin + p[1] * cos
    ]);

    geometry.setCoordinates([transformedPoints]);
    return geometry;
};

const selectionStyles = (feature, resolution) => {
    const selectionGeometry = fromExtent(bufferExtent(
        feature.getGeometry().getExtent(),
        resolution * 6
    ));

    return [
        new Style({
            geometry: selectionGeometry,
            stroke: new Stroke({
                color: '#ffffff',
                width: 5,
            }),
        }),
        new Style({
            geometry: selectionGeometry,
            stroke: new Stroke({
                color: '#f58220',
                width: 2,
                lineDash: [8, 5],
            }),
        }),
    ];
};


export const draw = {
    data () {
        return {
            drawSource: null,
            drawLayer: null,
            drawInteraction: null,
            helpTooltipElement: null,
            helpTooltipOverlay: null,
            pointerMoveKey: null,
            eraseClickKey: null,
            selectClickKey: null,
            selectionSource: null,
            selectionLayer: null,
            sketch: null,
            sketchChangeKey: null
        }
    },
    computed: {
        ...mapState('draw', ['undoStack', 'redoStack']),
        canUndo() { return this.undoStack.length > 0; },
        canRedo() { return this.redoStack.length > 0; }
    },
    methods: {
        ...mapActions('draw', [
            'pushFeature',
            'pushDeletion',
            'undo',
            'redo',
            'clearHistory'
        ]),
        initDrawLayer: function () {
            // La capa vive en variables de módulo para sobrevivir el cierre y la
            // reapertura de la herramienta: ToolContainer.vue la destruye con v-if
            // y exitDraw() sólo la oculta. Pero SÓLO vale si sigue siendo el mismo
            // mapa: al cambiar de mapa la capa heredada pertenece a un mapa ya
            // destruido y esta rama no la reenganchaba, así que lo dibujado y lo
            // importado se agregaban a una fuente fuera de todo mapa, sin ningún
            // error visible. Ese era el bug de "la importación no carga".
            if (persistentDrawLayer && persistentDrawMap === this.map) {
                this.drawLayer = persistentDrawLayer;
                this.drawSource = persistentDrawSource;
                this.drawLayer.setVisible(true);
                return;
            }
            // Mapa distinto: lienzo en blanco. La historia de deshacer guarda
            // features del mapa anterior y undo() los reinsertaría en la fuente
            // nueva, así que se limpia junto con la capa.
            if (persistentDrawLayer) {
                this.clearHistory();
            }
            // wrapX queda en su valor por defecto (true) a propósito: así el
            // renderer repite los dibujos en cada copia del mundo (ver
            // ol/renderer/canvas/VectorLayer.js:149-160), que es lo que hace que
            // al dar la vuelta al mundo se vea el MISMO mapa y no uno vacío. Lo
            // que rompía el dibujo tras dar la vuelta no era esto, sino que la
            // geometría quedaba con coordenadas de otro mundo y el replay group
            // sólo admite las del mundo real; se normaliza en
            // DrawTool.vue::wrapGeometryToWorld.
            this.drawSource = new VectorSource();
            this.drawLayer = new VectorLayer({
                source: this.drawSource,
                style: this.createDrawStyle(),
            });
            persistentDrawSource = this.drawSource;
            persistentDrawLayer = this.drawLayer;
            persistentDrawMap = this.map;
            this.map.addLayer(this.drawLayer)
        },
        initDrawInteraction: function (drawingType) {
            // Clean previous interaction and tooltip
            this.removeEraseInteraction();
            this.removeSelectInteraction();
            if (this.drawInteraction) {
                this.drawInteraction.abortDrawing();
                this.map.removeInteraction(this.drawInteraction);
            }
            this.removeHelpTooltip();

            let options = {
                source: this.drawSource,
                style: this.createDrawStyle(),
            };

            // Specialized interaction patterns
            if (drawingType === 'Freehand') {
                options.type = 'LineString'; 
                options.freehand = true;
            } else if (drawingType === 'Arrow') {
                options.type = 'LineString';
                options.maxPoints = 2;
                options.geometryFunction = arrowGeometryFunction;
            } else if (drawingType === 'Ellipse') {
                options.type = 'LineString';
                options.maxPoints = 2;
                options.geometryFunction = ellipseGeometryFunction;
            } else if (drawingType === 'Text') {
                options.type = 'Point';
            } else {
                options.type = drawingType;
            }

            this.drawInteraction = new Draw(options);
            this.map.addInteraction(this.drawInteraction);

            this.createHelpTooltip();
            this.pointerMoveKey = this.map.on('pointermove', this.handlePointerMove);

            this.drawInteraction.on('drawstart', (evt) => {
                if (this.lockLastEditableFeature) {
                    this.lockLastEditableFeature();
                }
                this.sketch = evt.feature;
                this.bindSketchMeasurement(evt.feature, drawingType);
            });

            this.drawInteraction.on('drawend', (evt) => {
                this.sketch = null;
                this.onDrawEnd(evt);
                this.clearSketchMeasurementListener();
            });
        },
        bindSketchMeasurement: function (feature, drawingType) {
            this.clearSketchMeasurementListener();
            if (
                !feature
                || !['Circle', 'Ellipse'].includes(drawingType)
                || !this.updateDrawingMeasurement
            ) {
                return;
            }

            const geometry = feature.getGeometry();
            if (!geometry) return;

            const updateMeasurement = () => {
                this.updateDrawingMeasurement(feature, drawingType);
            };
            this.sketchChangeKey = geometry.on('change', updateMeasurement);
            updateMeasurement();
        },
        clearSketchMeasurementListener: function () {
            if (this.sketchChangeKey) {
                unByKey(this.sketchChangeKey);
                this.sketchChangeKey = null;
            }
            if (this.clearDrawingMeasurement) {
                this.clearDrawingMeasurement();
            }
        },
        createHelpTooltip: function () {
            if (this.helpTooltipElement) {
                this.helpTooltipElement.parentNode.removeChild(this.helpTooltipElement);
            }
            this.helpTooltipElement = document.createElement('div');
            this.helpTooltipElement.className = 'tooltip hidden';
            this.helpTooltipOverlay = new Overlay({
                element: this.helpTooltipElement,
                offset: [15, 0],
                positioning: 'center-left',
            });
            this.map.addOverlay(this.helpTooltipOverlay);
        },
        removeHelpTooltip: function () {
            if (this.helpTooltipOverlay) {
                this.map.removeOverlay(this.helpTooltipOverlay);
                this.helpTooltipOverlay = null;
            }
            if (this.helpTooltipElement && this.helpTooltipElement.parentNode) {
                this.helpTooltipElement.parentNode.removeChild(this.helpTooltipElement);
                this.helpTooltipElement = null;
            }
            if (this.pointerMoveKey) {
                unByKey(this.pointerMoveKey);
                this.pointerMoveKey = null;
            }
            this.clearSketchMeasurementListener();
            this.sketch = null;
        },
        initEraseInteraction: function () {
            if (this.drawInteraction) {
                this.drawInteraction.abortDrawing();
                this.map.removeInteraction(this.drawInteraction);
                this.drawInteraction = null;
            }
            this.removeEraseInteraction();
            this.removeSelectInteraction();
            this.removeHelpTooltip();

            this.createHelpTooltip();
            this.pointerMoveKey = this.map.on('pointermove', this.handlePointerMove);
            this.eraseClickKey = this.map.on('singleclick', (evt) => {
                const feature = this.map.forEachFeatureAtPixel(
                    evt.pixel,
                    candidate => candidate,
                    {
                        layerFilter: layer => layer === this.drawLayer,
                        hitTolerance: 12,
                    }
                );
                this.eraseFeature(feature);
            });
        },
        removeEraseInteraction: function () {
            if (this.eraseClickKey) {
                unByKey(this.eraseClickKey);
                this.eraseClickKey = null;
            }
        },
        initSelectInteraction: function () {
            if (this.drawInteraction) {
                this.drawInteraction.abortDrawing();
                this.map.removeInteraction(this.drawInteraction);
                this.drawInteraction = null;
            }
            this.removeEraseInteraction();
            this.removeSelectInteraction();
            this.removeHelpTooltip();
            this.initSelectionLayer();

            this.createHelpTooltip();
            this.pointerMoveKey = this.map.on('pointermove', this.handlePointerMove);
            this.selectClickKey = this.map.on('singleclick', (evt) => {
                const feature = this.map.forEachFeatureAtPixel(
                    evt.pixel,
                    candidate => candidate,
                    {
                        layerFilter: layer => layer === this.drawLayer,
                        hitTolerance: 12,
                    }
                );

                if (feature && this.onFeatureSelected) {
                    this.onFeatureSelected(feature);
                } else if (this.clearSelectedFeature) {
                    this.clearSelectedFeature();
                }
            });
        },
        removeSelectInteraction: function () {
            if (this.selectClickKey) {
                unByKey(this.selectClickKey);
                this.selectClickKey = null;
            }
        },
        initSelectionLayer: function () {
            if (this.selectionLayer) {
                this.selectionLayer.setVisible(true);
                return;
            }

            this.selectionSource = new VectorSource();
            this.selectionLayer = new VectorLayer({
                source: this.selectionSource,
                style: selectionStyles,
            });
            this.selectionLayer.setZIndex(10000);
            this.map.addLayer(this.selectionLayer);
        },
        refreshSelectionHighlight: function (feature) {
            this.initSelectionLayer();
            this.selectionSource.clear();
            if (!feature || !feature.getGeometry()) return;

            const highlight = feature.clone();
            highlight.setGeometry(feature.getGeometry().clone());
            highlight.setStyle(null);
            this.selectionSource.addFeature(highlight);
        },
        clearSelectionHighlight: function () {
            if (this.selectionSource) {
                this.selectionSource.clear();
            }
        },
        destroySelectionLayer: function () {
            if (!this.selectionLayer || !this.map) return;
            this.map.removeLayer(this.selectionLayer);
            this.selectionLayer = null;
            this.selectionSource = null;
        },
        eraseFeature: function (feature) {
            if (!feature || !this.drawSource) return;
            this.drawSource.removeFeature(feature);
            this.pushDeletion(feature);
            if (this.onFeatureErased) {
                this.onFeatureErased(feature);
            }
        },
        handlePointerMove: function (evt) {
            if (evt.dragging || !this.helpTooltipElement) {
                return;
            }
            let helpMsg = 'Haga click para empezar a dibujar';
            const type = this.currentDrawingType;

            if (this.activeMode === 'erase') {
                helpMsg = 'Haga click o toque una figura para borrarla';
            } else if (this.activeMode === 'select') {
                helpMsg = 'Haga click o toque una figura para editar su estilo';
            } else if (this.sketch) {
                if (type === 'LineString' || type === 'Polygon') {
                    helpMsg = 'Haga click para continuar. Doble click para finalizar.';
                } else if (type === 'Circle') {
                    helpMsg = 'Mueva el cursor para definir el radio y haga click para finalizar.';
                } else if (type === 'Arrow' || type === 'Ellipse') {
                    helpMsg = 'Haga click para finalizar.';
                }
            } else {
                if (type === 'Point') {
                    helpMsg = 'Haga click para situar el punto';
                } else if (type === 'Text') {
                    helpMsg = 'Haga click para situar el texto';
                } else if (type === 'Freehand') {
                    helpMsg = 'Haga click y arrastre para dibujar';
                } else if (type === 'Circle') {
                    helpMsg = 'Haga click para fijar el centro';
                }
            }

            this.helpTooltipElement.innerHTML = helpMsg;
            this.helpTooltipOverlay.setPosition(evt.coordinate);
            this.helpTooltipElement.classList.remove('hidden');
            },
            applyHistoryAction: function (entry, direction) {
            if (!entry) return;
            // Compatibility with history entries created before delete actions existed.
            const action = entry.feature ? entry : { type: 'add', feature: entry };
            const shouldAdd = direction === 'undo'
                ? action.type === 'delete'
                : action.type === 'add';

            if (shouldAdd) {
                if (!this.drawSource.hasFeature(action.feature)) {
                    this.drawSource.addFeature(action.feature);
                }
            } else if (this.drawSource.hasFeature(action.feature)) {
                this.drawSource.removeFeature(action.feature);
            }
            },
            undoAction: function () {
            if (this.lockLastEditableFeature) {
                this.lockLastEditableFeature();
            }
            if (this.clearSelectedFeature) {
                this.clearSelectedFeature();
            }
            this.undo().then(action => {
                this.applyHistoryAction(action, 'undo');
            });
            },
            redoAction: function () {
            if (this.lockLastEditableFeature) {
                this.lockLastEditableFeature();
            }
            if (this.clearSelectedFeature) {
                this.clearSelectedFeature();
            }
            this.redo().then(action => {
                this.applyHistoryAction(action, 'redo');
            });
            },
        clearAllDrawings: function () {
            if (this.lockLastEditableFeature) {
                this.lockLastEditableFeature();
            }
            if (this.clearSelectedFeature) {
                this.clearSelectedFeature();
            }
            this.drawSource.clear();
            this.clearHistory();
        },
    },
};
